import { useState, useEffect, useCallback } from "react";
import {
    TextField,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    TableSortLabel,
    Box,
    Grid2,
    Typography,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Icon,
    Tooltip
} from "@mui/material";
import Loader from "../../components/Loader";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
    countryActionID,
    DeactivateClientUrl,
    DeleteClientUrl,
    GetClientListSearchUrl,
    getFillListUrl
} from "../../utils/constants";
import { axiosDelete, axiosGet, axiosPost } from "../../utils/Axios";
import { useToast } from "../../components/toast";
import { HeaderBlock } from "@colliers/colliers-b2c";
import {
    isAdminRole,
    isDeleteAccountSuccess,
    NO_DATA_FOUND_MSG,
    normalizeClientStatus,
    resolveDeleteErrorMessage
} from "./clientDisableOptions";

const FILTER_INPUT_CLASS = "padding-top-s list-page-control-client";
const FILTER_SELECT_CLASS = "padding-top-s list-page-control-client text-align-left";

export const parseClientDate = (dateStr) => {
    if (!dateStr || dateStr === "N/A") return null;
    const cleaned = String(dateStr).replace(/^GMT\s+/i, "").trim();
    const match = /^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2}):(\d{2}))?$/.exec(cleaned);
    if (match) {
        const [, day, month, year, hour = "00", minute = "00", second = "00"] = match;
        return Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    }
    const parsed = Date.parse(cleaned);
    return Number.isNaN(parsed) ? null : parsed;
};

const compareNullableDates = (timeA, timeB) => {
    if (timeA === null && timeB === null) return 0;
    if (timeA === null) return -1;
    if (timeB === null) return 1;
    return timeA - timeB;
};

const compareClientItems = (a, b, sortBy) => {
    if (sortBy === "createdDate" || sortBy === "lastLoginDate") {
        return compareNullableDates(parseClientDate(a?.[sortBy]), parseClientDate(b?.[sortBy]));
    }
    if (sortBy === "activeDashboard") {
        return (Number(a?.activeDashboard) || 0) - (Number(b?.activeDashboard) || 0);
    }
    const valA = String(a?.[sortBy] ?? "");
    const valB = String(b?.[sortBy] ?? "");
    return valA.localeCompare(valB, undefined, { sensitivity: "base" });
};

export const sortClientList = (list, sortBy, sortOrder) => {
    if (!Array.isArray(list)) return [];
    return [...list].sort((a, b) => {
        const comparison = compareClientItems(a, b, sortBy);
        return sortOrder === "asc" ? comparison : -comparison;
    });
};

const resolveStatusValue = (status) => {
    if (status === "Active") return true;
    if (status === "" || status === "0") return null;
    return false;
};

const buildSearchPayload = ({ userName, emailAddress, status, country, inactiveSinceDate }) => ({
    Name: userName || "",
    EmailAddress: emailAddress || "",
    Status: resolveStatusValue(status),
    BaseCountryID: country !== "" && country !== "0" ? Number(country) : null,
    InactiveSinceDate: inactiveSinceDate !== "" ? inactiveSinceDate : null
});

const renderClientActionButtons = ({
    userID,
    isRowActive,
    isAdminUser,
    deactivateAccountText,
    deleteAccountText,
    viewText,
    onDeactivate,
    onDeleteAccount,
    onEdit
}) => {
    const editOrViewTitle = isRowActive ? "Edit" : viewText;
    const editOrViewIconClass = isRowActive
        ? "fa-light fa-pen-to-square font-size-base text-primary"
        : "fa-light fa-eye font-size-base text-primary";

    return (
        <Box className="display-flex justify-content-center client-action-buttons">
            {isRowActive && (
                <Button
                    className="padding-right-none"
                    onClick={() => onDeactivate(userID)}
                >
                    <Tooltip title={deactivateAccountText}>
                        <Icon
                            className="fa-light fa-user-slash font-size-base text-primary"
                        />
                    </Tooltip>
                </Button>
            )}

            {isAdminUser && (
                <Button
                    className="padding-right-none"
                    onClick={() => onDeleteAccount(userID)}
                >
                    <Tooltip title={deleteAccountText}>
                        <Icon
                            className="fa-light fa-trash-can font-size-base text-primary"
                        />
                    </Tooltip>
                </Button>
            )}

            <Button
                className="padding-right-none"
                onClick={() => onEdit(userID)}
            >
                <Tooltip title={editOrViewTitle}>
                    <Icon className={editOrViewIconClass} />
                </Tooltip>
            </Button>
        </Box>
    );
};

const renderClientListTableRows = ({
    list,
    neverLoggedInText,
    noRecordFoundText,
    deactivateAccountText,
    deleteAccountText,
    viewText,
    isAdminUser,
    onDeactivate,
    onDeleteAccount,
    onEdit
}) => {
    if (!list.length) {
        return (
            <TableRow>
                <TableCell colSpan={8}>{noRecordFoundText}</TableCell>
            </TableRow>
        );
    }
    return list.map((item) => {
        const displayStatus = normalizeClientStatus(item.status);
        const isRowActive = displayStatus.toLowerCase() === "active";
        return (
            <TableRow
                key={item.userID || item.emailAddress}
                className={isRowActive ? "" : "client-row-inactive"}
            >
                <TableCell>{item.emailAddress}</TableCell>
                <TableCell>{item.createdBy}</TableCell>
                <TableCell className="client-cell-nowrap">{item.createdDate}</TableCell>
                <TableCell>{item.country}</TableCell>
                <TableCell align="center">{item.activeDashboard}</TableCell>
                <TableCell>{displayStatus}</TableCell>
                <TableCell className="client-cell-nowrap">
                    {item.lastLoginDate || neverLoggedInText}
                </TableCell>
                <TableCell align="center">
                    {renderClientActionButtons({
                        userID: item.userID,
                        isRowActive,
                        isAdminUser,
                        deactivateAccountText,
                        deleteAccountText,
                        viewText,
                        onDeactivate,
                        onDeleteAccount,
                        onEdit
                    })}
                </TableCell>
            </TableRow>
        );
    });
};

const ClientUserList = () => {
    const { ToastComponent, showToast } = useToast();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleteModal, setIsDeleteModal] = useState(false);
    const [userID, setUserID] = useState("");
    const [isDeleteAccountModal, setIsDeleteAccountModal] = useState(false);
    const [deleteUserID, setDeleteUserID] = useState("");
    const [userList, setUserList] = useState([]);
    const [userName, setUserName] = useState("");
    const [emailAddress, setEmailAddress] = useState("");
    const [country, setCountry] = useState("");
    const [countryList, setCountryList] = useState([]);
    const [status, setStatus] = useState("Active");
    const [inactiveSinceDate, setInactiveSinceDate] = useState("");
    const [reset, setReset] = useState(false);
    const [sortBy, setSortBy] = useState("createdDate");
    const [sortOrder, setSortOrder] = useState("desc");

    const [currentPage, setCurrentPage] = useState(1);
    const listItemsPerPage = 10;
    const startIndex = (currentPage - 1) * listItemsPerPage;
    const sortedUserList = sortClientList(userList, sortBy, sortOrder);
    const paginatedUserList = sortedUserList.slice(startIndex, startIndex + listItemsPerPage);

    const TAction = t("common.Action");
    const TnoRecordFound = t("common.noRecordFound");
    const Tsearch = t("common.search");
    const Treset = t("common.reset");
    const Tcancel = t("common.Cancel");
    const Tdelete = t("common.Delete");
    const Tdeactivate = t("common.deactivate");
    const Tview = t("common.view");
    const Tstatus = t("common.status");
    const Tselect = t("common.select");
    const Tactive = t("common.active");
    const Tinactive = t("common.inactive");
    const TpageTitle = t("clientPage.pageTitle");
    const Temail = t("clientPage.Email");
    const TUser = t("clientPage.User");
    const TactiveDashboard = t("clientPage.activeDashboard");
    const TenterEmail = t("clientPage.addEmail");
    const TselectStatus = t("clientPage.selectStatus");
    const TcreatedBy = t("common.createdBy");
    const TcreatedDate = t("common.createdDate");
    const Tcountry = t("common.country");
    const TlastLoginDate = t("clientPage.lastLoginDate");
    const TneverLoggedIn = t("clientPage.neverLoggedIn");
    const TinactiveSinceDate = t("clientPage.inactiveSinceDate");
    const TdeactivateAccount = t("clientPage.deactivateAccount");
    const TdeactivateAccountConfirm = t("clientPage.deactivateAccountConfirmation");
    const TdeactivateAccountSuccess = t("clientPage.deactivateAccountSuccess");
    const TdeleteAccount = t("clientPage.deleteAccount");
    const TdeleteAccountConfirm = t("clientPage.deleteAccountConfirmation");
    const TdeleteAccountSuccess = t("clientPage.deleteAccountSuccess");
    const isAdminUser = isAdminRole(localStorage.getItem("userInfo"));

    const sortableColumns = [
        { id: "emailAddress", label: TUser },
        { id: "createdBy", label: TcreatedBy },
        { id: "createdDate", label: TcreatedDate },
        { id: "country", label: Tcountry },
        { id: "activeDashboard", label: TactiveDashboard, align: "center" },
        { id: "status", label: Tstatus },
        { id: "lastLoginDate", label: TlastLoginDate }
    ];

    const fetchCountries = useCallback(async () => {
        try {
            const data = await axiosGet(getFillListUrl, {
                actionID: countryActionID,
                valueID: null,
                keyValue: null
            });
            if (Array.isArray(data) && data.length > 0) {
                setCountryList(data);
            }
        } catch (error) {
            console.error("Error fetching country list:", error);
        }
    }, []);

    const getClientSearchList = useCallback(async () => {
        if (reset) {
            setReset(false);
        }
        const payload = buildSearchPayload({
            userName,
            emailAddress,
            status,
            country,
            inactiveSinceDate
        });
        try {
            setIsLoading(true);
            const response = await axiosPost(GetClientListSearchUrl, payload);
            if (response) {
                const normalizedList = Array.isArray(response)
                    ? response.map((item) => ({
                        ...item,
                        status: normalizeClientStatus(item.status)
                    }))
                    : response;
                setUserList(normalizedList);
                setCurrentPage(1);
            } else {
                console.error("Failed to fetch dashboards: No data found.");
            }
        } catch (error) {
            console.error("Error fetching dashboards:", error);
        } finally {
            setIsLoading(false);
        }
    }, [reset, userName, emailAddress, status, country, inactiveSinceDate]);

    useEffect(() => {
        const userInfo = localStorage.getItem("userInfo");
        if (!userInfo) {
            navigate("/login/root-data");
            return;
        }
        const extractAccess = JSON.parse(localStorage.getItem("matchedModule"));
        const readAccess = extractAccess?.read;
        if (!readAccess) {
            navigate("/access-denied");
            return;
        }
        fetchCountries();
        getClientSearchList();
    }, []);

    useEffect(() => {
        if (reset) {
            getClientSearchList();
        }
    }, [reset]);

    const filterSetters = {
        user: setUserName,
        email: setEmailAddress,
        country: setCountry,
        status: setStatus,
        inactiveSinceDate: setInactiveSinceDate
    };

    const handleFilterChange = (e, field) => {
        const setter = filterSetters[field];
        if (setter) {
            setter(e.target.value);
        }
    };

    const handleRequestSort = (column) => {
        const isAsc = sortBy === column && sortOrder === "asc";
        setSortOrder(isAsc ? "desc" : "asc");
        setSortBy(column);
    };

    const resetSearch = () => {
        setUserName("");
        setEmailAddress("");
        setCountry("");
        setStatus("Active");
        setInactiveSinceDate("");
        setReset(true);
        setCurrentPage(1);
    };

    const toggleDeleteModal = (targetUserID) => {
        setIsDeleteModal(!isDeleteModal);
        setUserID(isDeleteModal ? "" : targetUserID);
    };

    const toggleDeleteAccountModal = (targetUserID) => {
        setIsDeleteAccountModal(!isDeleteAccountModal);
        setDeleteUserID(isDeleteAccountModal ? "" : targetUserID);
    };

    const handleDeactivate = async (targetUserID) => {
        try {
            setIsLoading(true);
            const response = await axiosDelete(DeactivateClientUrl, {
                clientUserID: Number(targetUserID)
            });
            if (response) {
                setUserList((prev) =>
                    prev.map((u) =>
                        u.userID === targetUserID
                            ? { ...u, status: "Inactive", activeDashboard: 0 }
                            : u
                    )
                );
                showToast("success", TdeactivateAccountSuccess);
            } else {
                showToast("error", NO_DATA_FOUND_MSG);
            }
        } catch (error) {
            console.error(error);
            showToast("error", `Error deactivating client: ${error}`);
        } finally {
            setIsLoading(false);
            setIsDeleteModal(false);
        }
    };

    const handleDeleteAccount = async (targetUserID) => {
        try {
            setIsLoading(true);
            const response = await axiosDelete(DeleteClientUrl, {
                clientUserID: Number(targetUserID)
            });
            if (isDeleteAccountSuccess(response)) {
                setUserList((prev) => prev.filter((u) => u.userID !== targetUserID));
                showToast("success", TdeleteAccountSuccess);
            } else {
                showToast("error", resolveDeleteErrorMessage(response));
            }
        } catch (error) {
            console.error(error);
            showToast("error", `Error deleting client: ${error}`);
        } finally {
            setIsLoading(false);
            setIsDeleteAccountModal(false);
        }
    };

    const navigateEdit = (targetUserID) => {
        navigate("/client-user-management-manage", { state: { userID: targetUserID } });
    };

    return (
        <>
            {isLoading && <Loader />}
            <HeaderBlock left="" title={TpageTitle} leftCenterAlign="true" actionsCenterAlign="true" />

            <Box
                id="main"
                className="full-width padding-right-m padding-left-m padding-bottom-m padding-top-none clientListMainBox"
            >
                <Box className="pagetitle main-area-top text-align-center">
                    <section className="section page-body">
                        <Box>
                            <Grid2
                                container
                                spacing={2}
                                className="margin-top-base align-items-flex-end client-filter-bar"
                            >
                                <Grid2 size={{ xs: 12, sm: 6, md: 2.5 }}>
                                    <TextField
                                        fullWidth
                                        className={FILTER_INPUT_CLASS}
                                        label={Temail}
                                        variant="outlined"
                                        placeholder={TenterEmail}
                                        value={emailAddress}
                                        onChange={(e) => handleFilterChange(e, "email")}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12, sm: 6, md: 2.5 }}>
                                    <TextField
                                        fullWidth
                                        className={FILTER_SELECT_CLASS}
                                        label={Tcountry}
                                        variant="outlined"
                                        select
                                        value={country}
                                        onChange={(e) => handleFilterChange(e, "country")}
                                        slotProps={{
                                            select: {
                                                displayEmpty: true
                                            },
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    >
                                        <MenuItem value="">{Tselect}</MenuItem>
                                        {countryList.map((item) => (
                                            <MenuItem key={item.code} value={item.code}>
                                                {item.description}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid2>

                                <Grid2 size={{ xs: 12, sm: 6, md: 2.5 }}>
                                    <TextField
                                        fullWidth
                                        className={FILTER_SELECT_CLASS}
                                        label={Tstatus}
                                        variant="outlined"
                                        select
                                        placeholder={TselectStatus}
                                        value={status}
                                        onChange={(e) => handleFilterChange(e, "status")}
                                        slotProps={{
                                            select: {
                                                displayEmpty: true
                                            },
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    >
                                        <MenuItem value="">{Tselect}</MenuItem>
                                        <MenuItem value="Active">{Tactive}</MenuItem>
                                        <MenuItem value="Inactive">{Tinactive}</MenuItem>
                                    </TextField>
                                </Grid2>

                                <Grid2 size={{ xs: 12, sm: 6, md: 2.5 }}>
                                    <TextField
                                        fullWidth
                                        className={FILTER_INPUT_CLASS}
                                        label={TinactiveSinceDate}
                                        variant="outlined"
                                        type="date"
                                        value={inactiveSinceDate}
                                        onChange={(e) => handleFilterChange(e, "inactiveSinceDate")}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true
                                            }
                                        }}
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12, sm: 12, md: 2 }}>
                                    <Box className="display-flex client-filter-actions">
                                        <Button
                                            variant="contained"
                                            className="margin-right-s"
                                            onClick={getClientSearchList}
                                        >
                                            {Tsearch}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            onClick={resetSearch}
                                        >
                                            {Treset}
                                        </Button>
                                    </Box>
                                </Grid2>
                            </Grid2>

                            <TableContainer className="border list-height client-table-container">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            {sortableColumns.map((col) => (
                                                <TableCell
                                                    key={col.id}
                                                    align={col.align}
                                                    sortDirection={sortBy === col.id ? sortOrder : false}
                                                >
                                                    <TableSortLabel
                                                        active={sortBy === col.id}
                                                        direction={sortBy === col.id ? sortOrder : "asc"}
                                                        onClick={() => handleRequestSort(col.id)}
                                                    >
                                                        {col.label}
                                                    </TableSortLabel>
                                                </TableCell>
                                            ))}
                                            <TableCell align="center">{TAction}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {renderClientListTableRows({
                                            list: paginatedUserList,
                                            neverLoggedInText: TneverLoggedIn,
                                            noRecordFoundText: TnoRecordFound,
                                            deactivateAccountText: TdeactivateAccount,
                                            deleteAccountText: TdeleteAccount,
                                            viewText: Tview,
                                            isAdminUser,
                                            onDeactivate: toggleDeleteModal,
                                            onDeleteAccount: toggleDeleteAccountModal,
                                            onEdit: navigateEdit
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Dialog
                                className="border-radius-soft padding-y-base padding-x-base text-align-center clientListDialog"
                                open={isDeleteModal}
                                onClose={() => toggleDeleteModal("")}
                            >
                                <DialogTitle>
                                    <Typography
                                        className="text-primary text-align-left font-bold"
                                        variant="h6"
                                        component="span"
                                    >
                                        {TdeactivateAccount}
                                    </Typography>
                                </DialogTitle>
                                <DialogContent>
                                    <Typography className="margin-bottom-base text-primary font-size-h5 text-align-left">
                                        {TdeactivateAccountConfirm}
                                    </Typography>
                                </DialogContent>
                                <DialogActions className="dialogAction">
                                    <Button
                                        variant="outlined"
                                        onClick={() => toggleDeleteModal("")}
                                        className="clientListDialogActionBtn"
                                    >
                                        {Tcancel}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={() => handleDeactivate(userID)}
                                        className="clientListDialogActionBtn1"
                                    >
                                        {Tdeactivate}
                                    </Button>
                                </DialogActions>
                            </Dialog>

                            <Dialog
                                className="border-radius-soft padding-y-base padding-x-base text-align-center clientListDialog"
                                open={isDeleteAccountModal}
                                onClose={() => toggleDeleteAccountModal("")}
                            >
                                <DialogTitle>
                                    <Typography
                                        className="text-primary text-align-left font-bold"
                                        variant="h6"
                                        component="span"
                                    >
                                        {TdeleteAccount}
                                    </Typography>
                                </DialogTitle>
                                <DialogContent>
                                    <Typography className="margin-bottom-base text-primary font-size-h5 text-align-left">
                                        {TdeleteAccountConfirm}
                                    </Typography>
                                </DialogContent>
                                <DialogActions className="dialogAction">
                                    <Button
                                        variant="outlined"
                                        onClick={() => toggleDeleteAccountModal("")}
                                        className="clientListDialogActionBtn"
                                    >
                                        {Tcancel}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={() => handleDeleteAccount(deleteUserID)}
                                        className="clientListDialogActionBtn1"
                                    >
                                        {Tdelete}
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Box>
                    </section>
                </Box>
            </Box>
            {ToastComponent}
        </>
    );
};

export default ClientUserList;
